/**
 * API Timeout Configuration
 *
 * Provides adaptive timeout values for different API endpoints based on complexity.
 * This prevents 60-second timeouts on slow dashboard queries while maintaining
 * fast timeouts for simple requests.
 *
 * @module api-timeouts
 */

export const API_TIMEOUTS = {
  /** Default timeout for most API endpoints (60 seconds) */
  DEFAULT: 60000,

  /** Extended timeout for regular dashboard queries (2 minutes) */
  DASHBOARD_REGULAR: 120000,

  /** Maximum timeout for complex dashboard queries with heavy filtering (3 minutes) */
  DASHBOARD_CLICKHOUSE: 180000,

  /** Timeout for file uploads and long-running operations (5 minutes) */
  LONG_RUNNING: 300000,
};

/**
 * Feature flags for timeout behavior
 * Set to false to rollback to original 60s timeouts
 */
export const TIMEOUT_FEATURE_FLAGS = {
  /** Enable extended timeouts for dashboard (true = use adaptive timeouts) */
  USE_EXTENDED_TIMEOUTS: true,

  /** Enable smart detection based on filter complexity (true = analyze filters) */
  USE_SMART_DETECTION: true,

  /** Show progressive feedback after 30s (true = show "taking longer" notification) */
  SHOW_PROGRESS_FEEDBACK: true,
};

/**
 * Determine appropriate timeout for dashboard query based on filter complexity
 *
 * @param filters - Dashboard filter object
 * @returns Timeout in milliseconds
 *
 * @example
 * // Simple query - 120s timeout
 * getDashboardTimeout({ page: 1, page_size: 50 });
 *
 * // Complex query - 180s timeout
 * getDashboardTimeout({
 *   jsonb_filter: [{ field: 'invoice_number', value: '12345' }],
 *   date_range_days: 730,
 *   advanced_search: ['tag1', 'tag2', 'tag3', 'tag4', 'tag5', 'tag6']
 * });
 */
export function getDashboardTimeout(filters?: any): number {
  // Feature flag: If extended timeouts disabled, use default
  if (!TIMEOUT_FEATURE_FLAGS.USE_EXTENDED_TIMEOUTS) {
    return API_TIMEOUTS.DEFAULT;
  }

  // Feature flag: If smart detection disabled, use regular dashboard timeout
  if (!TIMEOUT_FEATURE_FLAGS.USE_SMART_DETECTION) {
    return API_TIMEOUTS.DASHBOARD_REGULAR;
  }

  // No filters provided - use regular timeout
  if (!filters) {
    return API_TIMEOUTS.DASHBOARD_REGULAR;
  }

  // Detect complexity indicators
  const hasComplexFilters =
    // JSONB field filtering (extraction_fields queries are slow)
    (filters.jsonb_filter && filters.jsonb_filter.length > 0) ||
    // Large date ranges (>1 year require scanning many records)
    (filters.date_range_days && filters.date_range_days > 365) ||
    // Many advanced search terms (requires multiple LIKE queries)
    (filters.advanced_search && filters.advanced_search.length > 5) ||
    // Custom SQL filters (unpredictable performance)
    (filters.custom_query && filters.custom_query.length > 0) ||
    // Multiple status filters (requires complex OR logic)
    (filters.status_filters && filters.status_filters.length > 3);

  return hasComplexFilters
    ? API_TIMEOUTS.DASHBOARD_CLICKHOUSE
    : API_TIMEOUTS.DASHBOARD_REGULAR;
}

/**
 * Get human-readable timeout description for UI feedback
 *
 * @param timeoutMs - Timeout in milliseconds
 * @returns User-friendly description
 *
 * @example
 * getTimeoutDescription(120000); // "2 minutes"
 * getTimeoutDescription(180000); // "3 minutes"
 */
export function getTimeoutDescription(timeoutMs: number): string {
  const seconds = Math.floor(timeoutMs / 1000);
  const minutes = Math.floor(seconds / 60);

  if (minutes > 0) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }

  return `${seconds} second${seconds !== 1 ? 's' : ''}`;
}

/**
 * Calculate warning threshold (when to show "taking longer" notification)
 * Shows after 50% of timeout has elapsed
 *
 * @param timeoutMs - Timeout in milliseconds
 * @returns Warning threshold in milliseconds (default: 30s)
 */
export function getWarningThreshold(timeoutMs: number): number {
  if (!TIMEOUT_FEATURE_FLAGS.SHOW_PROGRESS_FEEDBACK) {
    return Infinity; // Never show warning if feature disabled
  }

  // Show warning after 50% of timeout, but at least 30s
  return Math.max(30000, timeoutMs * 0.5);
}
